// HERO "Padrão com busca por IA" (modelo_busca = 10): é o hero 1 (WelcomePieces) COMPLETO —
// banner/vídeo, frase, cartão de filtros, avançado, cards de busca — com uma barra de
// frase livre no TOPO do cartão. Os filtros clássicos ficam RECOLHIDOS: só aparecem (com o
// divisor "ou use os filtros") quando o visitante clica em "Avançado" (Daniel 26/08). Nada é duplicado do
// hero 1: ele recebe a barra pelo slot `aiSearch`; a lógica da IA é o mesmo useBuscaIa do
// hero 4. O default export é um ErrorBoundary com `fallback` (hero 1 puro) pra qualquer
// erro da barra não derrubar a home.
import React from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '../../context/Info';
import { ensureReadableTextColor, isColorLight } from '../../../utils/FunctionsGlobals';
import { genMetaEventId, trackMetaEvent } from '../../../utils/metaPixel';
import { categoriaBuscaDe } from '../../../utils/buscaCategoriaFixa';
import { appendCodsiteQuery } from '../../../utils/url';
import type { HeroSectionProps } from '../../registry/types';
import WelcomePieces, { ISelectors } from '../welcome/WelcomePieces';
import { SparkIcon, useBuscaIa } from '../welcome-ia/useBuscaIa';
import styles from './WelcomePadraoIa.module.css';

/** Texto do campo de frase livre (era o cabeçalho acima do input até 26/08). */
const PLACEHOLDER_IA = 'Busca por IA: descreva o imóvel com suas palavras';
/** Telas estreitas cortariam o texto completo ("...com suas pala"): lá o prefixo sai — o ícone
 *  de faísca dentro do campo e o botão "Buscar com IA" já dizem que a busca é por IA. */
const PLACEHOLDER_IA_CURTO = 'Descreva o imóvel com suas palavras';

/** cs2619 (10/09): no toggle acima da busca, o CLIQUE já busca. Comprar/Locação/Temporada abrem a
 *  lista daquela transação (ou buscam a frase já digitada, na transação clicada) e "Lançamentos"
 *  abre /condominios com os empreendimentos em lançamento — o mesmo destino do "Ver todos" da
 *  vitrine de lançamentos da home. Gate por tenant: nos outros sites o toggle só seleciona e a
 *  busca sai no botão, como antes. */
const CODSITES_TOGGLE_BUSCA_NO_CLIQUE = new Set<number>([2619]);

// Exportada para o hero 12 (welcome-carrossel-ia), que planta a MESMA barra no cartao do
// hero 3. Reuso, nao copia: uma barra so, um CSS so, um useBuscaIa so.
export interface IBarraBuscaIaProps {
    allCities: ISelectors['allCities'];
    allTypes: ISelectors['allTypes'];
    allTransacao: ISelectors['allTransacao'];
    site: any;
    origin?: any;
    codsite?: any;
    codsite2?: any;
    valores?: any;
    /** transação do select clássico do hero 1 (venda | aluguel | temporada) */
    transacao: string;
    /** filtros clássicos visíveis (visitante clicou em "Avançado") → mostra o divisor "ou use os filtros" */
    filtrosAbertos: boolean;
    /** clique no toggle Comprar/Alugar/Temporada → troca a transação do hero 1 (fonte única) */
    onTransacaoChange: (_value: string) => void;
    /** categoria fixada pela 4ª opção do toggle ("Lançamentos"), ou null.
     *  OPCIONAL: o hero 12 (welcome-carrossel-ia) planta a mesma barra no hero 3, que não tem
     *  esse estado — lá a barra segue com as 3 transações de sempre. */
    categoriaFixa?: number | null;
    /** liga/desliga a categoria fixa no hero 1 (fonte única, igual à transação) */
    onCategoriaFixaChange?: (_id: number | null) => void;
}

export const BarraBuscaIa: React.FC<IBarraBuscaIaProps> = ({
    allCities,
    allTypes,
    allTransacao,
    site,
    origin,
    codsite,
    codsite2,
    valores,
    transacao,
    filtrosAbertos,
    onTransacaoChange,
    categoriaFixa = null,
    onCategoriaFixaChange,
}) => {
    const {
        texto,
        setTexto,
        loading,
        mensagem,
        desambiguacao,
        limparDesambiguacao,
        transacaoSel,
        opcoesTransacao,
        submit,
        escolherDesambiguacao,
    } = useBuscaIa({
        allCities,
        allTypes,
        allTransacao,
        site,
        origin,
        codsite,
        codsite2,
        valores,
        transacaoExterna: transacao,
        categoriaFixa,
    });

    // Placeholder curto no mobile. Começa no texto completo (igual ao SSR) e só troca depois de
    // montar, no cliente — assim não há divergência de hidratação.
    const [placeholderIa, setPlaceholderIa] = React.useState(PLACEHOLDER_IA);
    React.useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(max-width: 640px)');
        const aplica = () => setPlaceholderIa(mq.matches ? PLACEHOLDER_IA_CURTO : PLACEHOLDER_IA);
        aplica();
        mq.addEventListener('change', aplica);
        return () => mq.removeEventListener('change', aplica);
    }, []);

    // mesma régua de cor do botão "Buscar" do hero 1
    const buttonBackground = site?.cor2 || '#1e1e1e';
    const buttonPreferredTextColor = isColorLight(site?.cor2) ? site?.cor11 : site?.cor13;
    const buttonTextColor = ensureReadableTextColor(buttonPreferredTextColor, buttonBackground) || '#ffffff';

    const executarBusca = () => {
        limparDesambiguacao();
        if (texto.trim()) {
            const eventId = genMetaEventId();
            try { sessionStorage.setItem('meta:searchEventId', JSON.stringify({ id: eventId, at: Date.now() })); } catch { /* sessionStorage indisponível — Search sai sem dedup c/ CAPI */ }
            trackMetaEvent(site, 'Search', {
                search_string: texto,
                transaction_type: transacaoSel,
            }, { eventId });
        }
        submit();
    };

    const onSubmitForm = (event: React.FormEvent) => {
        event.preventDefault();
        executarBusca();
    };

    // Busca no clique do toggle (CODSITES_TOGGLE_BUSCA_NO_CLIQUE). A transação escolhida vive no
    // hero 1 (fonte única) e só chega aqui no render SEGUINTE — chamar o submit() dentro do onClick
    // buscaria na transação antiga. Por isso o clique só LIGA o pedido e este efeito dispara a busca
    // no render em que `transacaoSel` já tem o valor novo (o submit desse render enxerga ele).
    const buscaNoClique = CODSITES_TOGGLE_BUSCA_NO_CLIQUE.has(Number(site?.id));
    const [buscarAposEscolha, setBuscarAposEscolha] = React.useState(false);
    // Com a busca no clique, a barra NÃO mostra transação pré-selecionada (cs2619, 10/09: "está
    // vindo com Comprar já selecionada, não deixe nenhuma selecionada"). A transação padrão do
    // tenant segue valendo por baixo (busca por frase no "Buscar com IA"); só a pílula some. O botão
    // clicado acende enquanto a busca carrega — é o retorno visual do clique.
    const [clicado, setClicado] = React.useState<string | null>(null);
    React.useEffect(() => {
        if (!buscarAposEscolha) return;
        setBuscarAposEscolha(false);
        executarBusca();
        // executarBusca é recriada a cada render; o que importa é rodar no render da escolha nova.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buscarAposEscolha]);
    const router = useRouter();
    const { init_props } = useAuth();
    // "Lançamentos" → /condominios só com os empreendimentos em lançamento: os mesmos ids do
    // "Ver todos" da vitrine da home (home_ventures_ver_todos_ids, recorte da categoria no SSR).
    // Sem os ids (falha do SSR) abre a /condominios inteira em vez de travar o botão.
    const abrirLancamentos = () => {
        const ids: number[] = Array.isArray(init_props?.home_ventures_ver_todos_ids)
            ? init_props.home_ventures_ver_todos_ids
            : [];
        router.push(appendCodsiteQuery(ids.length ? `/condominios?ids=${ids.join(',')}` : '/condominios', codsite2, origin));
    };

    // Toggle de transação ACIMA do campo, como no hero 4 (pedido do cs2619, 27/08): no hero 10 o
    // select clássico fica recolhido em "Avançado", e sem isto toda frase sem "alugar"/"temporada"
    // saía como venda. Mesmas opções/ordem/rótulos do hero 4 (`opcoesTransacao` = site.transacoes).
    // Só aparece com mais de uma transação configurada. Os botões são filhos DIRETOS do grupo:
    // o CSS de tenant `[data-builder-section="busca"] button:nth-child(2)` (cs2619 → "Locação")
    // continua caindo no "Alugar", como cai no hero 4.
    const mostrarToggleTransacao = opcoesTransacao.length > 1;
    // 4ª opção do toggle: uma CATEGORIA (cs2619 → "Lançamentos"), irmã de Comprar/Locação/
    // Temporada mas fixando `?categoria=<id>` em vez de trocar a transação. Gate por tenant em
    // utils/buscaCategoriaFixa — nos ~300 sites `catBusca` é null e nada muda.
    const catBusca = onCategoriaFixaChange ? categoriaBuscaDe(site?.id) : null;
    const lancamentoAtivo = catBusca != null && categoriaFixa === catBusca.id;
    const lancamentoMarcado = buscaNoClique ? clicado === 'lancamentos' : lancamentoAtivo;
    // cs2619 (31/08): cliente pediu os nomes SEM a "tarja branca" — botão inativo fica
    // transparente (só o ativo ganha a pílula na cor do tenant).
    const toggleSemTarja = Number(site?.id) === 2619;

    return (
        <div className={styles.barra}>
            {mostrarToggleTransacao ? (
                <div
                    className={buscaNoClique ? `${styles.linhaTransacoes} ${styles.hoverNaCorAtiva}` : styles.linhaTransacoes}
                    // hover na cor do "Buscar com IA" (ver .hoverNaCorAtiva no CSS) — só no gate
                    style={
                        buscaNoClique
                            ? ({ '--toggle-cor-ativa': buttonBackground, '--toggle-texto-ativo': buttonTextColor } as React.CSSProperties)
                            : undefined
                    }
                >
                {catBusca ? (
                    // ⚠️ Fica FORA do `.transacoes` DE PROPÓSITO. O CSS do Builder do cs2619 tem
                    // `[data-builder-section="busca"] button:nth-child(2)::after { content:"Locação" }`
                    // reescrevendo o 2º botão do grupo (o "Alugar"). Um 4º botão à esquerda DENTRO
                    // do grupo empurraria "Comprar" para a 2ª posição e ele passaria a exibir
                    // "Locação". Aqui o botão é filho único do próprio container: a contagem do
                    // grupo não muda e o dado do cliente (o CSS publicado) fica intocado.
                    <div className={styles.categoriaWrap}>
                        <button
                            type="button"
                            className={styles.transacao}
                            onClick={() => {
                                if (buscaNoClique) {
                                    setClicado('lancamentos');
                                    abrirLancamentos();
                                    return;
                                }
                                onCategoriaFixaChange?.(lancamentoAtivo ? null : catBusca.id);
                            }}
                            aria-pressed={lancamentoMarcado}
                            disabled={loading}
                            style={
                                lancamentoMarcado
                                    ? { background: buttonBackground, color: buttonTextColor, borderColor: buttonBackground }
                                    : toggleSemTarja
                                        ? { background: 'transparent', borderColor: 'transparent' }
                                        : undefined
                            }
                        >
                            {catBusca.rotulo}
                        </button>
                    </div>
                ) : null}
                <div className={styles.transacoes} role="group" aria-label="Tipo de negócio">
                    {opcoesTransacao.map((opt) => {
                        // Com a categoria ligada nenhuma transação fica marcada: quem manda na
                        // barra é o "Lançamentos" (a transação segue valendo na URL, no select).
                        // Com a busca no clique (cs2619) nada vem marcado: só o botão clicado acende.
                        const ativo = buscaNoClique ? clicado === opt.v : !lancamentoAtivo && transacaoSel === opt.v;
                        return (
                            <button
                                key={opt.v}
                                type="button"
                                className={styles.transacao}
                                onClick={() => {
                                    onCategoriaFixaChange?.(null);
                                    onTransacaoChange(opt.v);
                                    if (buscaNoClique) {
                                        setClicado(opt.v);
                                        setBuscarAposEscolha(true);
                                    }
                                }}
                                aria-pressed={ativo}
                                disabled={loading}
                                style={
                                    ativo
                                        ? { background: buttonBackground, color: buttonTextColor, borderColor: buttonBackground }
                                        : toggleSemTarja
                                            ? { background: 'transparent', borderColor: 'transparent' }
                                            : undefined
                                }
                            >
                                {opt.l}
                            </button>
                        );
                    })}
                </div>
                </div>
            ) : null}
            {/* (26/08, Daniel) O cabeçalho "Busca por IA · descreva o imóvel com suas palavras" saiu
                de cima do campo e virou o PLACEHOLDER — o ícone de faísca dentro do input mantém a
                identidade da IA. O exemplo com a cidade do tenant (hook) fica só no hero 4. */}
            <form className={styles.form} onSubmit={onSubmitForm}>
                <label htmlFor="welcome-padrao-ia-texto" className={styles.visually_hidden}>
                    Descreva o imóvel que você procura
                </label>
                <div className={styles.inputWrap}>
                    <SparkIcon size={18} className={styles.inputIcone} />
                    <input
                        id="welcome-padrao-ia-texto"
                        className={styles.input}
                        type="text"
                        inputMode="search"
                        autoComplete="off"
                        placeholder={placeholderIa}
                        value={texto}
                        disabled={loading}
                        onChange={(e) => setTexto(e.target.value)}
                        // O hero 1 escuta Enter no DOCUMENT e clica no "Buscar" clássico (#myLink).
                        // Enter aqui é da IA: segura a propagação pra não dispararem as duas buscas.
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.stopPropagation();
                        }}
                    />
                </div>
                <button
                    type="submit"
                    className={styles.botao}
                    disabled={loading}
                    style={{ background: buttonBackground, color: buttonTextColor, borderColor: buttonBackground }}
                    aria-label="Buscar imóveis com inteligência artificial"
                >
                    {loading ? <span className={styles.spinner} aria-hidden="true" /> : <SparkIcon size={16} />}
                    {loading ? 'Entendendo sua busca...' : 'Buscar com IA'}
                </button>
            </form>
            <div className={styles.status} role="status" aria-live="polite">
                {mensagem}
            </div>
            {desambiguacao ? (
                <ul className={styles.chips}>
                    {desambiguacao.opcoes.map((op, idx) => (
                        <li key={idx}>
                            <button
                                type="button"
                                className={styles.chip}
                                disabled={loading}
                                onClick={() => escolherDesambiguacao(op)}
                                title={op.label}
                            >
                                {op.label}
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {filtrosAbertos ? (
                <div className={styles.divisor} aria-hidden="true">
                    <span>ou use os filtros</span>
                </div>
            ) : null}
        </div>
    );
};

// Ligado pelo registry (NEW_HERO[10] em components/registry/home.ts) — recebe o superset
// de props dos heros da home; o hero 1 (WelcomePieces) consome o que lhe cabe.
export type IWelcomePadraoIaProps = HeroSectionProps;

// `fallback` é do ErrorBoundary (abaixo) — não desce pro hero 1
const heroPropsDe = (props: IWelcomePadraoIaProps): ISelectors => {
    const { fallback, ...heroProps } = props;
    void fallback;
    return heroProps as ISelectors;
};

const WelcomePadraoIaHero: React.FC<IWelcomePadraoIaProps> = (props) => {
    const heroProps = heroPropsDe(props);
    const { allCities, allTypes, allTransacao, site, origin, codsite, codsite2, valores } = heroProps;
    return (
        <WelcomePieces
            {...heroProps}
            aiSearch={({ transacao, filtrosAbertos, onTransacaoChange, categoriaFixa, onCategoriaFixaChange }) => (
                <BarraBuscaIa
                    filtrosAbertos={filtrosAbertos}
                    onTransacaoChange={onTransacaoChange}
                    categoriaFixa={categoriaFixa}
                    onCategoriaFixaChange={onCategoriaFixaChange}
                    allCities={allCities}
                    allTypes={allTypes}
                    allTransacao={allTransacao}
                    site={site}
                    origin={origin}
                    codsite={codsite}
                    codsite2={codsite2}
                    valores={valores}
                    transacao={transacao}
                />
            )}
        />
    );
};

// ErrorBoundary: se a barra de IA quebrar por QUALQUER motivo, cai no hero 1 PURO (o
// `fallback` da home, se vier; senão o próprio WelcomePieces sem a barra) — a home nunca
// fica sem hero.
class WelcomePadraoIa extends React.Component<IWelcomePadraoIaProps, { hasError: boolean }> {
    constructor(props: IWelcomePadraoIaProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch() {
        // silencioso de propósito: hero degrada pro padrão sem quebrar a home
    }

    render() {
        const { fallback, site } = this.props;
        if (!site) return fallback ?? null;
        if (this.state.hasError) return fallback ?? <WelcomePieces {...heroPropsDe(this.props)} />;
        return <WelcomePadraoIaHero {...this.props} />;
    }
}

export default WelcomePadraoIa;
